import { ImgHTMLAttributes, useState } from 'react';
import { cn } from '@/lib/utils';

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string;
  alt: string; // Obligatoire pour accessibilité
  fallbackSrc?: string; // Fallback si WebP non supporté
  loading?: 'lazy' | 'eager';
  aspectRatio?: string; // ex: "16/9", "4/3", "1/1"
  className?: string;
}

/**
 * Composant Image optimisé avec :
 * - Support WebP automatique avec fallback
 * - Lazy loading par défaut
 * - Placeholder pendant le chargement
 * - Aspect ratio pour éviter le layout shift
 */
export function OptimizedImage({
  src,
  alt,
  fallbackSrc,
  loading = 'lazy',
  aspectRatio,
  className,
  ...props
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Convertir automatiquement en WebP si disponible
  const webpSrc = src.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  const imageSrc = hasError && fallbackSrc ? fallbackSrc : webpSrc;

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-aspexit-gray-light',
        className
      )}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      {/* Placeholder skeleton pendant le chargement */}
      {!isLoaded && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-aspexit-gray-light via-gray-200 to-aspexit-gray-light" />
      )}

      {/* Image */}
      <img
        src={imageSrc}
        alt={alt}
        loading={loading}
        onLoad={() => setIsLoaded(true)}
        onError={(e) => {
          // Si WebP échoue, essayer le fallback ou l'original
          if (!hasError) {
            setHasError(true);
            const target = e.target as HTMLImageElement;
            target.src = fallbackSrc || src;
          }
        }}
        className={cn(
          'w-full h-full object-cover transition-opacity duration-300',
          isLoaded ? 'opacity-100' : 'opacity-0'
        )}
        {...props}
      />
    </div>
  );
}

/**
 * Composant pour les images responsive (srcset)
 * Génère plusieurs tailles pour différentes résolutions d'écran
 */
interface ResponsiveImageProps extends OptimizedImageProps {
  sizes?: string; // ex: "(max-width: 768px) 100vw, 50vw"
  widths?: number[]; // ex: [320, 640, 1024, 1920]
}

export function ResponsiveImage({
  src,
  alt,
  sizes = '100vw',
  widths = [320, 640, 1024, 1920],
  ...props
}: ResponsiveImageProps) {
  // Pour l'instant, on utilise juste OptimizedImage
  // Dans le futur, on pourrait générer des srcset avec différentes tailles
  return <OptimizedImage src={src} alt={alt} {...props} />;
}
